import { useEffect, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import Confetti from 'react-confetti'
import { CheckCircle, CircleX, Languages } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  AnchoredPopover,
  LmsButton,
  SafeHtml,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/ui'
import { LearningLessonShellFooter } from '@features/learning/shared/LearningLessonShell'
import type { LearningTemplateContinueActionsProps } from './learning-template-contract'
import { useLearningEnterContinue } from './learning-template-shortcuts'
import './learning-template-feedback.css'

const LEARNING_RESULT_IMAGES = {
  correct: '/images/learning-success.webp',
  incorrect: '/images/learning-wrong.webp',
} as const

export function LearningTemplateContinueActions({
  continuePending = false,
  continuePendingLabel,
  embedded = false,
  incorrectReviewHtml,
  incorrectReviewText,
  incorrectReviewTranslationText,
  isMobileOrTablet = false,
  onContinue,
  resultMessage,
  result,
}: LearningTemplateContinueActionsProps) {
  return (
    <>
      {result === 'incorrect' ? (
        <LearningTemplateIncorrectNote
          html={incorrectReviewHtml}
          embedded={embedded}
          isMobileOrTablet={isMobileOrTablet}
          text={incorrectReviewText}
          translationText={incorrectReviewTranslationText}
        />
      ) : null}
      <LearningTemplateExerciseResultBar
        continuePending={continuePending}
        continuePendingLabel={continuePendingLabel}
        embedded={embedded}
        onNext={() => onContinue(result)}
        resultMessage={resultMessage}
        result={result}
      />
    </>
  )
}

export function LearningTemplateCompletionConfetti() {
  const [viewport, setViewport] = useState(() => ({
    height: typeof window === 'undefined' ? 0 : window.innerHeight,
    width: typeof window === 'undefined' ? 0 : window.innerWidth,
  }))

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const handleResize = () => {
      setViewport({ height: window.innerHeight, width: window.innerWidth })
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <Confetti
      className="legacySelfStudyCompletionConfetti"
      height={viewport.height}
      numberOfPieces={200}
      recycle={false}
      width={viewport.width}
    />
  )
}

export function LearningTemplateExerciseResultBar({
  continuePending = false,
  continuePendingLabel,
  embedded = false,
  onNext,
  result,
  resultMessage,
}: {
  readonly continuePending?: boolean
  readonly continuePendingLabel?: ReactNode
  readonly embedded?: boolean
  readonly onNext: () => void
  readonly result: 'correct' | 'incorrect'
  readonly resultMessage?: ReactNode
}) {
  const { t } = useTranslation('book')
  const isCorrect = result === 'correct'
  useLearningEnterContinue(onNext, !continuePending)

  return (
    <LearningLessonShellFooter
      className={`learningTemplateExerciseResultBar legacySelfStudyExerciseResultBar${
        embedded ? ' learningTemplateExerciseResultBarEmbedded' : ''
      }`}
    >
      <div className={`learningTemplateExerciseResultContent legacySelfStudyExerciseResultContent ${result}`}>
        <img alt="" src={LEARNING_RESULT_IMAGES[result]} />
        <span>
          {isCorrect ? <CheckCircle aria-hidden="true" /> : <CircleX aria-hidden="true" />}
          {resultMessage ?? (isCorrect ? `${t('Correct')}!` : `${t('Not quite right')}!`)}
        </span>
      </div>
      <LmsButton
        className={`learningTemplateExerciseResultButton legacySelfStudyExerciseResultButton ${result}`}
        disabled={continuePending}
        isLoading={continuePending}
        loadingLabel={continuePendingLabel ?? t('Saving')}
        onClick={onNext}
        type="button"
        variant={isCorrect ? 'success' : 'destructive'}
      >
        {t('Continue')}
      </LmsButton>
    </LearningLessonShellFooter>
  )
}

export function LearningTemplateIncorrectNote({
  embedded = false,
  html,
  isMobileOrTablet,
  text,
  translationText,
}: {
  readonly embedded?: boolean
  readonly html?: string | null
  readonly isMobileOrTablet: boolean
  readonly text?: string | null
  readonly translationText?: string | null
}) {
  const { t } = useTranslation('book')

  if (!html && !text) {
    return null
  }

  return (
    <div
      className={`legacySelfStudyIncorrectNote${
        embedded ? ' learningTemplateIncorrectNoteEmbedded' : ''
      }`}
    >
      <div>
        <strong>{t('Correct answer')}</strong>
        {html ? (
          <SafeHtml className="legacySelfStudyAuthoredHtml" html={html} />
        ) : (
          <p className="legacySelfStudyIncorrectAnswerLine">
            <span>{text}</span>
            {translationText ? (
              <LearningTemplateTextTranslation
                isMobileOrTablet={isMobileOrTablet}
                text={translationText}
              />
            ) : null}
          </p>
        )}
      </div>
    </div>
  )
}

function LearningTemplateTextTranslation({
  isMobileOrTablet,
  text,
}: {
  readonly isMobileOrTablet: boolean
  readonly text: string
}) {
  const { t } = useTranslation('book')
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const content = (
    <div className="legacySelfStudyTextTranslationContent">
      <p><strong>{t('Does that mean')}:</strong></p>
      <p>{text}</p>
    </div>
  )
  const triggerIcon = <Languages aria-hidden="true" className="legacySelfStudyTextTranslationIcon" />

  if (isMobileOrTablet) {
    return (
      <>
        <LmsButton
          aria-label={t('Does that mean')}
          className="legacySelfStudyTextTranslationButton"
          onClick={(event: ReactMouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
          size="icon-sm"
          type="button"
          variant="ghost"
        >
          {triggerIcon}
        </LmsButton>
        <AnchoredPopover
          anchorEl={anchorEl}
          anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
          className="legacySelfStudyTextTranslationPopover"
          onClose={() => setAnchorEl(null)}
          open={Boolean(anchorEl)}
          transformOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        >
          {content}
        </AnchoredPopover>
      </>
    )
  }

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <LmsButton
            aria-label={t('Does that mean')}
            className="legacySelfStudyTextTranslationButton"
            size="icon-sm"
            type="button"
            variant="ghost"
          >
            {triggerIcon}
          </LmsButton>
        </TooltipTrigger>
        <TooltipContent className="legacySelfStudyTextTranslationTooltip" side="top">
          {content}
          <TooltipArrow className="legacySelfStudyTextTranslationTooltipArrow" />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
